import type {
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  IsGroupOpenByDefaultParams,
  RowClassRules,
} from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import type { BlotterTableClientLocalFilter, ColumnOrColumnGroup } from '..';
import { useCallbackRef } from '../../../hooks/useCallbackRef';
import { WarningSeverity } from '../../../types';
import { WARNING_ROW_CLASSNAME } from '../../AgGrid';
import { DEFAULT_BLOTTER_AGG_FUNCS } from '../aggFuncs';
import { useBlotterTableContext, type BlotterTableContextProps } from '../BlotterTableContext';
import { BlotterDensity } from '../types';
import { alphabeticalGroupOrder, quickFilterParser, resetColumnsIfAllHidden, safeGridApi } from '../utils';
import { DEFAULT_AG_GRID_EXCEL_STYLES } from './excelExportStyling';
import type { UseBlotterTableProps } from './types';
import { useBlotterTableMenus, type UseBlotterTableMenusParams } from './useBlotterTableMainMenu';

/** UseBlotterTable's gridOption's prop intends to represent a GridOptions object to the developer,
 * but there are limited properties that useBlotterTable modifies behavior of, so this protects from that usage
 *
 * Props excluded:
 * - context: the BlotterTableContextProps is always included, so the main prop adds to it
 * - getRowId: the rowID prop is used for data transaction processing, so needed for useBlotterTable
 * - onSortChanged: useBlotterTable specially handles our sort +/- key format, so this is overridden for now (candidate for refactoring)
 * - colDefs: useBlotterTable utilizes our custom Column definition, so would cause a conflict
 * - isExternalFilterPresent, doesExternalFilterPass: clientLocalFilter overrides this behavior
 */
type UseBlotterTableGridOptions<TRow> = Omit<
  GridOptions<TRow>,
  'context' | 'getRowId' | 'onSortChanged' | 'colDefs' | 'isExternalFilterPresent' | 'doesExternalFilterPass'
>;

export interface UseBlotterTableGridOptionsProps<TRow> extends Pick<
  UseBlotterTableProps<TRow>,
  'rowID' | 'density' | 'context' | 'onBlotterContextChange' | 'persistence' | 'suppressContextChangeRefreshForce'
> {
  /** Custom GridOptions provided with useBlotterTable hook call */
  gridOptions: UseBlotterTableGridOptions<TRow> | undefined;

  // The rest of the props are useBlotterTable inputs used to customize the gridOptions
  clientLocalFilter?: BlotterTableClientLocalFilter<TRow>;
  columns: ColumnOrColumnGroup[];
  autoGroupColumnDef?: GridOptions<TRow>['autoGroupColumnDef'];
  columnDefs: NonNullable<GridOptions['columnDefs']>;
  customBlotterContext: UseBlotterTableProps<TRow>['context'];
  getParamsFormatted: GridOptions['processCellForClipboard'];
  onGridReady: NonNullable<GridOptions<TRow>['onGridReady']>;
  rowID: UseBlotterTableProps<TRow>['rowID'];
  exportOptions: UseBlotterTableMenusParams<TRow>['exportOptions'];
  columnDynamicsParams?: UseBlotterTableProps<TRow>['columnDynamicsParams'];
}

// memoization and customization of AgGrid GridOptions {@link GridOptions}
export function useBlotterTableGridOptions<TRow>({
  gridOptions,
  clientLocalFilter,
  columns,
  columnDefs,
  customBlotterContext,
  density,
  getParamsFormatted,
  onGridReady,
  rowID,
  suppressContextChangeRefreshForce,
  columnDynamicsParams,
  persistence,
  onBlotterContextChange,
  exportOptions,
}: UseBlotterTableGridOptionsProps<TRow>): GridOptions<TRow> {
  const [api, setApi] = useState<GridApi | null>(null);
  const {
    rowHeightBlotterTableCompact,
    rowHeightBlotterTableDefault,
    rowHeightBlotterTableComfortable,
    rowHeightBlotterTableVeryComfortable,
  } = useTheme();
  const resolvedRowHeight =
    gridOptions?.rowHeight ??
    (density === BlotterDensity.Compact
      ? rowHeightBlotterTableCompact
      : density === BlotterDensity.Comfortable
        ? rowHeightBlotterTableComfortable
        : density === BlotterDensity.VeryComfortable
          ? rowHeightBlotterTableVeryComfortable
          : rowHeightBlotterTableDefault);

  // memoize the onGridReady callback in case someone else wants to use it
  // if user provides their own onGridReady, we need to call both
  // - useCallbackRef used to avoid recreating the function on every render for custom gridOptions
  const onGridReadyCallback = useCallbackRef<NonNullable<GridOptions['onGridReady']>>(params => {
    setApi(params.api);
    // run feature-provided onGridReady
    gridOptions?.onGridReady?.(params);
    // run useGridBlottersTable's onGridReady
    onGridReady?.(params);
  });

  const rowClassRules: RowClassRules<TRow> | undefined = useMemo(() => {
    if (columns.find(c => c.type === 'warning' && c.hide !== true)) {
      return {
        [WARNING_ROW_CLASSNAME]: params =>
          (
            params?.data as {
              warningSeverity?: WarningSeverity;
            }
          )?.warningSeverity === WarningSeverity.HIGH,
      };
    }
    return undefined;
  }, [columns]);

  const getRowId = useCallback<NonNullable<GridOptions['getRowId']>>(
    ({ data }) => (rowID ? data?.[rowID] : null),
    [rowID]
  );

  const { getMainMenuItems, getContextMenuItems } = useBlotterTableMenus<TRow>({
    gridApi: safeGridApi(api),
    getExtraMainMenuItems: gridOptions?.getMainMenuItems,
    getContextMenuItems: gridOptions?.getContextMenuItems,
    exportOptions,
    columnDynamicsParams,
  });

  // Context
  const blotterTableContext = useBlotterTableContext();
  const agGridContext = useRef<BlotterTableContextProps | null>(null);
  useEffect(() => {
    agGridContext.current = {
      ...blotterTableContext,
      ...customBlotterContext,
    };
    if (api) {
      api.refreshCells({ force: suppressContextChangeRefreshForce ? false : true });
      onBlotterContextChange?.({ api });
    }
  }, [api, blotterTableContext, customBlotterContext, onBlotterContextChange, suppressContextChangeRefreshForce]);

  // - useCallbackRef used to avoid recreating the function on every render for custom gridOptions
  const onFirstDataRendered: GridOptions<TRow>['onFirstDataRendered'] = useCallbackRef(
    (params: FirstDataRenderedEvent<TRow>) => {
      // run feature-provided onFirstDataRendered
      gridOptions?.onFirstDataRendered?.(params);
      // Check on mount if all columns are hidden. If they are, we are in a bad state, so reset them.
      resetColumnsIfAllHidden(params);
    }
  );

  const isGroupOpenByDefault = useIsGroupOpenByDefault({ persistence, gridOptions });

  const memoizedGridOptions = useMemo(() => {
    const outputOptions: GridOptions<TRow> = {
      ...gridOptions,
      columnDefs,
      autoGroupColumnDef: gridOptions?.autoGroupColumnDef,
      initialState: persistence?.initialState?.gridState,

      isGroupOpenByDefault,
      groupDefaultExpanded: undefined, // the behavior this property enables is baked into our implementation of isGroupOpenByDefault
      onFirstDataRendered,
      onGridReady: onGridReadyCallback,
      // Our column defs are unstable, and we use them to represent initial setup of columns. When column defs change, maintain the current column order set by the user.
      maintainColumnOrder: gridOptions?.maintainColumnOrder ?? true,
      animateRows: gridOptions?.animateRows ?? false,
      aggFuncs: { ...DEFAULT_BLOTTER_AGG_FUNCS, ...gridOptions?.aggFuncs },
      suppressAggFuncInHeader: gridOptions?.suppressAggFuncInHeader ?? true,
      rowHeight: resolvedRowHeight,
      rowClassRules: gridOptions?.rowClassRules ?? rowClassRules,
      getRowId,
      getMainMenuItems,
      getContextMenuItems,
      isExternalFilterPresent: () => clientLocalFilter !== undefined,
      doesExternalFilterPass: node => clientLocalFilter?.(node) ?? true,
      cacheQuickFilter: true,
      quickFilterParser: gridOptions?.quickFilterParser ?? quickFilterParser,
      initialGroupOrderComparator: gridOptions?.initialGroupOrderComparator ?? alphabeticalGroupOrder,
      // @ts-expect-error - The context should always be set
      context: agGridContext,
      processCellForClipboard: gridOptions?.processCellForClipboard ?? getParamsFormatted,
      copyHeadersToClipboard: true,
      popupParent: gridOptions?.popupParent ?? document.body,
      // Default styles come first, then user-provided styles (user styles can override defaults with same id)
      excelStyles: [...DEFAULT_AG_GRID_EXCEL_STYLES, ...(gridOptions?.excelStyles ?? [])],
    };
    return outputOptions;
  }, [
    gridOptions,
    persistence?.initialState?.gridState,
    columnDefs,
    isGroupOpenByDefault,
    onFirstDataRendered,
    onGridReadyCallback,
    resolvedRowHeight,
    rowClassRules,
    getRowId,
    getMainMenuItems,
    getContextMenuItems,
    getParamsFormatted,
    clientLocalFilter,
  ]);

  return memoizedGridOptions;
}

/**
 * This hook encapsulates custom behavior for how we want to handle group expansions in AgGrid. There are a few things we make possible that AgGrid does not natively support:
 * 1. Making "groupDefaultExpanded"-like behavior apply for more than just the first mount. We want it to keep applying until the user changes their expansion setting.
 * 2. Persisting expansion intent (expand-all, collapse-all) as a global state + exceptions instead of solely a list of all expanded IDs (which can become very large)
 */
const useIsGroupOpenByDefault = <TRow>({
  persistence,
  gridOptions,
}: Pick<UseBlotterTableProps<TRow>, 'persistence' | 'gridOptions'>) => {
  const persistedRowGroupExpansionState = persistence?.state?.extraGridState?.rowGroupExpansion?.expansionState;
  const persistedExpansionExceptions = persistence?.state?.extraGridState?.rowGroupExpansion?.expansionExceptions;
  const persistedExpansionExceptionsSet = useMemo(
    () => (persistedExpansionExceptions ? new Set(persistedExpansionExceptions) : undefined),
    [persistedExpansionExceptions]
  );

  const { isGroupOpenByDefault: inputIsGroupOpenByDefault, groupDefaultExpanded } = gridOptions ?? {};

  const isGroupOpenByDefault = useCallback(
    (params: IsGroupOpenByDefaultParams<TRow>) => {
      // If the user has provided their own isGroupOpenByDefault callback, we always let this take precedence
      if (inputIsGroupOpenByDefault?.(params) === true) {
        return true;
      }

      // If expansion state is persisted, use that
      if (persistedRowGroupExpansionState) {
        const defaultIsOpen = persistedRowGroupExpansionState === 'all-expanded';

        if (params.rowNode.id && persistedExpansionExceptionsSet?.has(params.rowNode.id)) {
          return !defaultIsOpen;
        }

        return defaultIsOpen;
      }

      // Else, nothing is persisted, check if there is a groupDefaultExpanded provided into gridOptions
      if (groupDefaultExpanded) {
        if (groupDefaultExpanded === -1) {
          // -1 means all levels are to be opened
          return true;
        }

        if (typeof groupDefaultExpanded === 'number' && params.level < groupDefaultExpanded) {
          // level specificity
          return true;
        }
      }

      return false;
    },
    [inputIsGroupOpenByDefault, groupDefaultExpanded, persistedExpansionExceptionsSet, persistedRowGroupExpansionState]
  );

  return isGroupOpenByDefault;
};
